/*
 * Copyright © 2017 TEKTELIC Communications Inc.
 */

import angularStorage from 'angular-storage';

export default angular.module('tektelic.api.user', [angularStorage])
    .factory('userService', UserService)
    .name;

/*@ngInject*/
function UserService($http, $q, $rootScope, $cookies, store, jwtHelper, $translate, $state, types, keycloakService) {
    var currentUser = null,
        userLoaded = false;

    var refreshTokenQueue = [];

    var service = {
        deleteUser: deleteUser,
        getAuthority: getAuthority,
        isAuthenticated: isAuthenticated,
        getCurrentUser: getCurrentUser,
        getSystemAndProviderUsers: getSystemAndProviderUsers,
        getProviderUsers: getProviderUsers,
        getProviderAndCustomerUsers: getProviderAndCustomerUsers,
        getCustomerUsers: getCustomerUsers,
        getCustomerAndSubCustomerUsers: getCustomerAndSubCustomerUsers,
        getSubCustomerUsers: getSubCustomerUsers,
        getCurrentSubCustomerUsers: getCurrentSubCustomerUsers,
        getUser: getUser,
        isUserLoaded: isUserLoaded,
        saveUser: saveUser,
        sendActivationEmail: sendActivationEmail,
        getActivationLink: getActivationLink,
        setUserFromJwtToken: setUserFromJwtToken,
        getJwtToken: getJwtToken,
        clearJwtToken: clearJwtToken,
        isJwtTokenValid : isJwtTokenValid,
        validateJwtToken: validateJwtToken,
        refreshJwtToken: refreshJwtToken,
        refreshTokenPending: refreshTokenPending,
        updateAuthorizationHeader: updateAuthorizationHeader,
        gotoDefaultPlace: gotoDefaultPlace,
        forceDefaultPlace: forceDefaultPlace,
        logout: logout,
        reloadUser: reloadUser
    }

    reloadUser();

    return service;

    function reloadUser() {
        userLoaded = false;
        loadUser(true).then(function success() {
            notifyUserLoaded();
        }, function fail() {
            notifyUserLoaded();
        });
    }

    function updateAndValidateToken(token, prefix, notify) {
        var valid = false;
        var tokenData = jwtHelper.decodeToken(token);
        var issuedAt = tokenData.iat;
        var expTime = tokenData.exp;
        if (issuedAt && expTime) {
            var ttl = expTime - issuedAt;
            if (ttl > 0) {
                var clientExpiration = new Date().valueOf() + ttl*1000;
                store.set(prefix, token);
                store.set(prefix + '_expiration', clientExpiration);
                valid = true;
            }
        }
        if (!valid && notify) {
            $rootScope.$broadcast('unauthenticated');
        }
    }

    function clearTokenData() {
        store.remove('jwt_token');
        store.remove('jwt_token_expiration');
        store.remove('refresh_token');
        store.remove('refresh_token_expiration');
        store.remove('id_token');
        store.remove('id_token_expiration');
        $cookies.remove('jwt-access-token')
    }

    function setUserFromJwtToken(jwtToken, refreshToken, notify, doLogout, noRefresh, idToken, activateToken) {
        currentUser = null;
        if (!jwtToken) {
            const logoutIdToken = store.get('id_token');
            const logoutRefreshToken = store.get('refresh_token');
            const logoutAccessToken = store.get('jwt_token');
            // User-initiated first-party logout (doLogout): revoke this session's refresh-token family
            // server-side (RFC 7009 form-encoded) so the session can no longer be used. SSO users
            // (id_token present) log out via the Keycloak redirect below instead. Best-effort &
            // fire-and-forget; local state is cleared regardless. The access token is attached explicitly
            // because clearTokenData() (below) removes it from the store before the request interceptor
            // would otherwise read it.
            if (doLogout && !logoutIdToken && logoutRefreshToken && logoutAccessToken) {
                $http.post('/api/auth/revoke',
                    'token=' + encodeURIComponent(logoutRefreshToken) + '&token_type_hint=refresh_token', {
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'X-Authorization': 'Bearer ' + logoutAccessToken
                        }
                    }).catch(angular.noop);
            }
            clearTokenData();
            if (logoutIdToken) {
                keycloakService.logout(logoutIdToken, activateToken);
            } else if (notify) {
                $rootScope.$broadcast('unauthenticated', doLogout);
            }
        } else {
            updateAndValidateToken(jwtToken, 'jwt_token', true);
            if (!noRefresh) {
                updateAndValidateToken(refreshToken, 'refresh_token', true);
            }
            if (idToken) {
                updateAndValidateToken(idToken, 'id_token', false);
            }
            if (notify) {
                loadUser(false).then(function success() {
                    $rootScope.$broadcast('authenticated');
                }, function fail() {
                    $rootScope.$broadcast('unauthenticated');
                });
            } else {
                loadUser(false);
            }
        }
    }

    function isAuthenticated() {
        return store.get('jwt_token') && currentUser && types.authority[currentUser.authority];
    }

    function getJwtToken() {
        return store.get('jwt_token');
    }

    function logout() {
        clearJwtToken(true);
    }

    function clearJwtToken(doLogout) {
        setUserFromJwtToken(null, null, true, doLogout);
    }

    function isJwtTokenValid() {
        return isTokenValid('jwt_token');
    }

    function isTokenValid(prefix, bufferMs) {
        var clientExpiration = store.get(prefix + '_expiration');

        if (!clientExpiration) return false;

        if (bufferMs) {
            return (clientExpiration - bufferMs) > Date.now();
        } else {
            return clientExpiration > Date.now();
        }
    }

    function validateJwtToken(doRefresh, bufferMs) {
        var deferred = $q.defer();
        if (!isTokenValid('jwt_token', bufferMs)) {
            if (doRefresh) {
                refreshJwtToken().then(function success() {
                    deferred.resolve();
                }, function fail() {
                    deferred.reject();
                });
            } else {
                clearJwtToken(false);
                deferred.reject();
            }
        } else {
            deferred.resolve();
        }
        return deferred.promise;
    }

    function resolveRefreshTokenQueue(data) {
        for (var q=0; q < refreshTokenQueue.length;q++) {
            refreshTokenQueue[q].resolve(data);
        }
        refreshTokenQueue = [];
    }

    function rejectRefreshTokenQueue(message) {
        for (var q = 0; q < refreshTokenQueue.length; q++) {
            refreshTokenQueue[q].reject(message);
        }
        refreshTokenQueue = [];
    }

    function refreshTokenPending() {
        return refreshTokenQueue.length > 0;
    }

    function refreshJwtToken() {
            var deferred = $q.defer();
            refreshTokenQueue.push(deferred);
            if (refreshTokenQueue.length === 1) {
                var refreshToken = store.get('refresh_token');
                var refreshTokenValid = isTokenValid('refresh_token');
                if (!refreshTokenValid) {
                    clearJwtToken(false);
                    rejectRefreshTokenQueue($translate.instant('access.refresh-token-expired'));
                } else {
                    const isKeycloak = isKeycloakToken(jwtHelper.decodeToken(refreshToken));
                    if (isKeycloak) {
                        return keycloakService.refreshToken(refreshToken).then(function success(response) {
                            const token = response.access_token;
                            const refreshToken = response.refresh_token;
                            const idToken = response.id_token;
                            setUserFromJwtToken(token, refreshToken, false, false, false, idToken);
                            resolveRefreshTokenQueue(response);
                        }, function fail() {
                            clearJwtToken(false);
                            rejectRefreshTokenQueue($translate.instant('access.refresh-token-failed'));
                        })
                    } else {
                        var refreshTokenRequest = {
                            refreshToken: refreshToken
                        };
                        $http.post('/api/auth/token', refreshTokenRequest).then(function success(response) {
                            var token = response.data.token;
                            var refreshToken = response.data.refreshToken;
                            setUserFromJwtToken(token, refreshToken, false);
                            resolveRefreshTokenQueue(response.data);
                        }, function fail() {
                            clearJwtToken(false);
                            rejectRefreshTokenQueue($translate.instant('access.refresh-token-failed'));
                        });
                    }
                }
            }
            return deferred.promise;
    }

    function getCurrentUser() {
        return currentUser;
    }

    function getAuthority() {
        if (currentUser) {
            return currentUser.authority;
        } else {
            return '';
        }
    }

    function isUserLoaded() {
        return userLoaded;
    }

    function loadUser(doTokenRefresh) {

        var deferred = $q.defer();

        function procceedJwtTokenValidate() {
            validateJwtToken(doTokenRefresh).then(function success() {
                var jwtToken = store.get('jwt_token');
                currentUser = jwtHelper.decodeToken(jwtToken);
                var scopes = currentUser && currentUser.scopes && angular.fromJson(currentUser.scopes);

                if (scopes && scopes.length > 0) {
                    currentUser.authority = scopes[0];
                } else if (currentUser) {
                    currentUser.authority = "ANONYMOUS";
                }
                if (types.authority[currentUser.authority] && currentUser.userId) {
                    getUser(currentUser.userId, true).then(
                        function success() {
                            $rootScope.forceFullscreen = false;
                            deferred.resolve();
                        },
                        function fail() {
                            deferred.reject();
                            logout();
                        }
                    )
                } else {
                    deferred.reject();
                }
            }, function fail() {
                deferred.reject();
            });
        }
        if (!currentUser) {
            procceedJwtTokenValidate();
        } else {
            deferred.resolve();
        }
        return deferred.promise;
    }

    function notifyUserLoaded() {
        if (!userLoaded) {
            userLoaded = true;
            $rootScope.$broadcast('userLoaded');
        }
    }

    function isKeycloakToken(tokenData) {
        return tokenData.iss.indexOf('keycloak') > -1;
    }

    function updateAuthorizationHeader(headers) {
        var jwtToken = store.get('jwt_token');
        if (jwtToken) {
            const isKeycloak = isKeycloakToken(jwtHelper.decodeToken(jwtToken));
            if (isKeycloak) {
                headers['Authorization'] = 'Bearer ' + jwtToken;
            } else {
                headers['X-Authorization'] = 'Bearer ' + jwtToken;
            }
        }
        return jwtToken;
    }

    function getSystemAndProviderUsers() {
        var deferred = $q.defer();
        $http.get('/api/system/users', null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getProviderUsers(providerId) {
        var deferred = $q.defer();
        $http.get(`/api/provider/${providerId}/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getProviderAndCustomerUsers() {
        var deferred = $q.defer();
        $http.get(`/api/provider/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getCustomerUsers(customerId) {
        var deferred = $q.defer();
        $http.get(`/api/customer/${customerId}/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getCustomerAndSubCustomerUsers() {
        var deferred = $q.defer();
        $http.get(`/api/customer/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getSubCustomerUsers(subCustomerId) {
        var deferred = $q.defer();
        $http.get(`/api/subCustomer/${subCustomerId}/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getCurrentSubCustomerUsers() {
        var deferred = $q.defer();
        $http.get(`/api/subCustomer/users`, null).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function saveUser(user, sendActivationMail) {
        var deferred = $q.defer();
        var url = '/api/user';
        if (angular.isDefined(sendActivationMail)) {
            url += '?sendActivationMail=' + sendActivationMail;
        }
        $http.post(url, user).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getUser(userId, ignoreErrors) {
        var deferred = $q.defer();
        var url = '/api/user/' + userId;
        $http.get(url, { ignoreErrors: ignoreErrors }).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function deleteUser(userId) {
        var deferred = $q.defer();
        var url = '/api/user/' + userId;
        $http.delete(url).then(function success() {
            deferred.resolve();
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function sendActivationEmail(email) {
        var deferred = $q.defer();
        var url = '/api/user/sendActivationEmail';
        $http.post(url, {email: email}).then(function success(response) {
            deferred.resolve(response);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function getActivationLink(userId) {
        var deferred = $q.defer();
        var url = `/api/user/${userId}/activationLink`
        $http.get(url).then(function success(response) {
            deferred.resolve(response.data);
        }, function fail() {
            deferred.reject();
        });
        return deferred.promise;
    }

    function forceDefaultPlace(/*to, params*/) {
        return false;
    }

    function gotoDefaultPlace(params) {
        if (isAuthenticated()) {
            var place = 'home.links';
            $state.go(place, params);
        } else {
            $state.go('login', params);
        }
    }

}



// WEBPACK FOOTER //
// ./src/app/api/user.service.js